import { z } from "zod";

/**
 * TEK HATA BICIMI
 *
 * `kod` makine okur (istemci buna gore davranir), `mesaj` kullaniciya
 * gosterilir ve Turkce'dir.
 *
 * ================================================================
 * BU DOSYA 04.09.2026'YA KADAR GERCEGI ANLATMIYORDU
 * ================================================================
 * Basinda su cumle vardi: *"API'nin tum hata yanitlari bu semadan
 * gecer."* Iki bakimdan da yanlisti ve tasarim oturumu farketti:
 *
 *   1. `hataSemasi` ve `hataKoduSemasi` depoda HICBIR YERDE
 *      cagrilmiyordu. Hicbir yanit bu semadan gecmiyordu — sema
 *      yazilmis ama devreye alinmamisti.
 *
 *   2. `HATA_KODLARI` KAPALI bir liste (`as const` + `z.enum`) ve
 *      icinde sekiz kod vardi. Olculdu: `apps/api` icinde
 *      `kod: "..."` ile gonderilen 140'tan fazla ayri kod var.
 *      Yani liste gercegin yirmide birini tasiyordu.
 *
 * Kimse patlamiyordu cunku kimse dogrulamiyordu. Ama "sozlesme" diye
 * tuttugumuz bir dosyanin yalan soylemesi, bu depoda tekrar eden
 * hata sinifinin ta kendisi: mekanizma var, hicbir yol onu
 * kullanmiyor — ve okuyan onu isliyor saniyor.
 *
 * ================================================================
 * COZUM: KAPALI LISTE DEGIL, IKI KATMAN
 * ================================================================
 * Butun kodlari buraya tasimak da yanlis olurdu: 140 kod her yeni
 * ucta buyur, listeyi guncellemeyi unutan biri derleme hatasi alir
 * ve kodu listeye eklemek yerine `DOGRULAMA` yazmaya baslardi —
 * yani liste, ayrimi YOK EDEN bir baskiya donusurdu.
 *
 *   TABAN kodlar    her istemcinin tanimasi GEREKEN, tasima
 *                   duzeyindeki kodlar. Kimlik, yetki, hiz siniri...
 *                   Bunlar HER uctan gelebilir.
 *
 *   ALAN kodlari    tek bir isin kendi cevabi ("AY_DOLU",
 *                   "UZMAN_DEGIL", "BILET_GECERSIZ"). Onlari
 *                   tanimak o ekranin isi; taniyamayan ekran
 *                   `mesaj` alanini gosterir ve dogru davranir.
 *
 * `kod` bu yuzden `z.string()`: sema ALAN kodlarini reddetmiyor.
 * Reddetseydi, dogrulamayi devreye aldigimiz gun API'nin yarisi
 * kirilirdi.
 */

/**
 * Her istemcinin tanimasi gereken taban kodlar.
 *
 * Bunlar tasima duzeyinde: hangi uca giderseniz gidin gelebilirler
 * ve istemcinin davranisi ekrana degil KODA bagli (oturum dustuyse
 * giris ekrani, yetki yoksa 403 ekrani...).
 */
export const TABAN_HATA_KODLARI = [
  "DOGRULAMA", // gonderilen veri sema ile uyusmuyor
  "KIMLIK_YOK", // oturum yok veya suresi dolmus
  "YETKI_YOK", // oturum var, izin yok (403)
  "SUDO_GEREKLI", // hassas islem, yeniden kimlik dogrulama isteniyor
  "BULUNAMADI",
  "CAKISMA", // ayni kayit baskasi tarafindan degistirilmis
  "HIZ_SINIRI",
  "BAKIM_MODU", // planli bakim; istemci bakim ekranini ciziyor
  "SUNUCU_HATASI",
] as const;

export type TabanHataKodu = (typeof TABAN_HATA_KODLARI)[number];

/** Taban kod mu — istemci ozel bir davranis secmeden once soruyor. */
export function tabanHataKoduMu(kod: string): kod is TabanHataKodu {
  return (TABAN_HATA_KODLARI as readonly string[]).includes(kod);
}

/**
 * Hata yanitinin BICIMI.
 *
 * REFERANS SEMA — zorlayici degil. Bugun hicbir yanit bundan
 * gecmiyor ve bu bilincli olarak boyle birakildi: yanitlari tek tek
 * dogrulamaya almak butun modulleri tarayan bir degisiklik ve ayri
 * bir karar (`docs/dogrulama-listesi.md`, C6).
 *
 * Semanin bugunku isi ISTEMCI TARAFINDA: gelen govdenin beklenen
 * sekle uyup uymadigini sinamak isteyen bir ekran bunu kullanabilir.
 */
export const hataSemasi = z.object({
  /**
   * TABAN ya da ALAN kodu. `z.enum` DEGIL — gerekcesi dosyanin
   * basinda: kapali liste 140 kodun 9'unu tanir ve otekileri
   * gecersiz sayardi.
   */
  kod: z.string().min(1),
  mesaj: z.string(),
  /** Alan bazli dogrulama hatalari: { "email": "Gecerli bir e-posta girin" } */
  ayrinti: z.record(z.unknown()).optional(),
  /** Ilgili olayin izini surmek icin — destek talebinde bu numara istenir */
  istekId: z.string().optional(),
});

export type Hata = z.infer<typeof hataSemasi>;

/**
 * TABAN kodlarin varsayilan metinleri.
 *
 * ALAN kodlari burada YOK ve olmamali: her birinin kendi cumlesi
 * ucun icinde, olayin baglamiyla birlikte yaziliyor ("Hesap kilitli.
 * 12 dakika sonra deneyin." gibi). Buraya tasinsalardi baglam
 * kaybolurdu.
 *
 * Istemci once sunucunun `mesaj` alanini gosterir; bu tablo yalnizca
 * mesaj gelmediginde devreye giren yedek.
 */
export const HATA_METNI: Record<TabanHataKodu, string> = {
  DOGRULAMA: "Gönderilen bilgilerde eksik veya hatalı alan var.",
  KIMLIK_YOK: "Oturumunuz sona ermiş. Lütfen tekrar giriş yapın.",
  YETKI_YOK: "Bu işlem için yetkiniz yok.",
  SUDO_GEREKLI: "Bu işlem için şifrenizi yeniden girmeniz gerekiyor.",
  BULUNAMADI: "Aradığınız kayıt bulunamadı.",
  CAKISMA: "Bu kayıt siz görüntülerken başkası tarafından değiştirildi.",
  HIZ_SINIRI: "Çok fazla deneme yaptınız. Lütfen biraz bekleyin.",
  BAKIM_MODU: "Sistem şu anda planlı bakımda.",
  SUNUCU_HATASI: "Beklenmeyen bir hata oluştu. Sorun sürerse destek ile iletişime geçin.",
};
